<template>
  <div class="pub-inv" v-if="data">
    <div class="pub-inv__shell">
      <header class="pub-inv__hero">
        <div class="pub-inv__hero-top">
          <div class="pub-inv__brand">
            <img v-if="logoUrl" :src="logoUrl" alt="" class="pub-inv__logo">
            <div>
              <h1 class="pub-inv__company">{{ companyName }}</h1>
              <p v-if="companyAddress" class="pub-inv__company-meta">{{ companyAddress }}</p>
              <p v-if="companyPhone" class="pub-inv__company-meta">{{ companyPhone }}</p>
            </div>
          </div>
          <div class="pub-inv__hero-badge">
            <span :class="['pub-inv__status', 'pub-inv__status--' + (data.invoice.status || 'draft')]">
              {{ statusMeta(data.invoice.status).label }}
            </span>
          </div>
        </div>
        <div class="pub-inv__hero-bottom">
          <div>
            <span class="pub-inv__label">Nomor invoice</span>
            <strong class="pub-inv__inv-no">{{ data.invoice.invoice_number }}</strong>
          </div>
          <div class="pub-inv__dates">
            <div v-if="issueDateLabel">
              <span class="pub-inv__label">Tanggal</span>
              <span>{{ issueDateLabel }}</span>
            </div>
            <div v-if="dueDateLabel">
              <span class="pub-inv__label">Jatuh tempo</span>
              <span>{{ dueDateLabel }}</span>
            </div>
          </div>
        </div>
      </header>

      <main class="pub-inv__body">
        <div class="pub-inv__grid">
          <section class="pub-inv__card">
            <h2 class="pub-inv__section-title">Ditagihkan kepada</h2>
            <p class="pub-inv__customer-name">{{ data.invoice.customer_name || '—' }}</p>
          </section>
          <section class="pub-inv__card pub-inv__card--total">
            <h2 class="pub-inv__section-title">Total tagihan</h2>
            <p class="pub-inv__amount">{{ formatRp(data.invoice.total) }}</p>
            <p v-if="data.invoice.status === 'paid'" class="pub-inv__paid-note">
              <i class="fas fa-check-circle"></i> Pembayaran diterima
            </p>
          </section>
        </div>

        <section class="pub-inv__card pub-inv__card--table">
          <h2 class="pub-inv__section-title">Rincian</h2>
          <div class="pub-inv__table-wrap">
            <table class="pub-inv__table">
              <thead>
                <tr>
                  <th>Deskripsi</th>
                  <th class="pub-inv__col-qty">Qty</th>
                  <th class="pub-inv__col-num">Harga</th>
                  <th class="pub-inv__col-num">Jumlah</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(it, i) in data.invoice.items" :key="i">
                  <td>{{ it.description }}</td>
                  <td class="pub-inv__col-qty">{{ it.qty }}</td>
                  <td class="pub-inv__col-num">{{ formatRp(it.unit_price) }}</td>
                  <td class="pub-inv__col-num"><strong>{{ formatRp(lineAmount(it)) }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pub-inv__totals">
            <div class="pub-inv__totals-row">
              <span>Subtotal</span>
              <span>{{ formatRp(data.invoice.subtotal) }}</span>
            </div>
            <div v-if="data.invoice.discount" class="pub-inv__totals-row">
              <span>Diskon</span>
              <span>− {{ formatRp(data.invoice.discount) }}</span>
            </div>
            <div v-if="data.invoice.tax_amount" class="pub-inv__totals-row">
              <span>Pajak ({{ data.invoice.tax_percent || 0 }}%)</span>
              <span>{{ formatRp(data.invoice.tax_amount) }}</span>
            </div>
            <div class="pub-inv__totals-row pub-inv__totals-row--grand">
              <span>Total</span>
              <span>{{ formatRp(data.invoice.total) }}</span>
            </div>
          </div>
        </section>

        <section v-if="manualTransferBanks.length" class="pub-inv__card pub-inv__card--bank">
          <h2 class="pub-inv__section-title"><i class="fas fa-university"></i> Pembayaran transfer bank</h2>
          <p class="pub-inv__bank-lead">Silakan transfer sesuai nominal tagihan ke salah satu rekening berikut:</p>
          <div
            v-for="(bank, bIdx) in manualTransferBanks"
            :key="'bank-' + bIdx"
            class="pub-inv__bank-block"
          >
            <div v-if="manualTransferBanks.length > 1" class="pub-inv__bank-block-title">Rekening {{ bIdx + 1 }}</div>
            <div class="pub-inv__bank-rows">
              <div v-if="bank.bank_name" class="pub-inv__bank-row">
                <span>Bank</span>
                <strong>{{ bank.bank_name }}</strong>
              </div>
              <div v-if="bank.bank_account_number" class="pub-inv__bank-row">
                <span>No. rekening</span>
                <strong>{{ bank.bank_account_number }}</strong>
                <button type="button" class="pub-inv__copy" @click="copyText(bank.bank_account_number, 'Nomor rekening')">
                  Salin
                </button>
              </div>
              <div v-if="bank.bank_account_holder" class="pub-inv__bank-row">
                <span>Atas nama</span>
                <strong>{{ bank.bank_account_holder }}</strong>
              </div>
            </div>
          </div>
          <p v-if="manualTransferNotes" class="pub-inv__bank-notes">{{ manualTransferNotes }}</p>
        </section>

        <section v-if="data.invoice.notes" class="pub-inv__card">
          <h2 class="pub-inv__section-title">Catatan invoice</h2>
          <p class="pub-inv__notes">{{ data.invoice.notes }}</p>
        </section>

        <section v-if="qrisPayload" class="pub-inv__card pub-inv__card--qris">
          <h2 class="pub-inv__section-title"><i class="fas fa-qrcode"></i> Pembayaran QRIS</h2>
          <p class="pub-inv__qris-hint">Scan dengan e-wallet / m-banking, atau salin string di bawah.</p>
          <textarea readonly class="pub-inv__qris-code" rows="4" :value="qrisPayload"></textarea>
          <button type="button" class="pub-inv__btn pub-inv__btn--ghost pub-inv__btn--block" @click="copyQris">
            Salin kode QRIS
          </button>
        </section>
      </main>

      <footer class="pub-inv__actions">
        <a :href="pdfUrl" class="pub-inv__btn pub-inv__btn--outline" target="_blank" rel="noopener">
          <i class="fas fa-file-pdf"></i> Unduh PDF
        </a>
        <button
          v-if="showPayButton"
          type="button"
          class="pub-inv__btn pub-inv__btn--primary"
          :disabled="paying"
          @click="pay"
        >
          <i class="fas fa-wallet"></i>
          {{ paying ? 'Memuat…' : 'Bayar sekarang' }}
        </button>
      </footer>

      <p class="pub-inv__footer-note">Invoice digital · StarSender</p>
    </div>
  </div>

  <div v-else-if="loadError" class="pub-inv pub-inv--center">
    <div class="pub-inv__shell pub-inv__shell--narrow">
      <div class="pub-inv__card text-center py-5 px-4">
        <div class="pub-inv__error-icon"><i class="fas fa-file-excel"></i></div>
        <h2 class="h5 font-weight-bold">Invoice tidak ditemukan</h2>
        <p class="text-muted mb-0">Link tidak valid atau sudah tidak aktif.</p>
      </div>
    </div>
  </div>

  <div v-else class="pub-inv pub-inv--center">
    <div class="text-center">
      <div class="spinner-border text-light mb-3" role="status"></div>
      <p class="text-white-50 small mb-0">Memuat invoice…</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { formatRp, statusMeta } from '@/utils/invoiceUi';

function publicInvoiceApiBase() {
  const base = (axios.defaults.baseURL || process.env.VUE_APP_BASE_URL || '').replace(/\/api\/v1\/?$/, '');
  return base || 'https://api.starsender.online';
}

function formatMs(ms) {
  if (!ms) return '';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default {
  data() {
    return { data: null, qrisPayload: '', paying: false, token: '', loadError: false };
  },
  computed: {
    companyName() {
      return this.data?.setting?.company_name || 'Invoice';
    },
    companyAddress() {
      return this.data?.setting?.company_address || '';
    },
    companyPhone() {
      return this.data?.setting?.company_phone || '';
    },
    logoUrl() {
      return this.data?.setting?.company_logo_url || '';
    },
    issueDateLabel() {
      return formatMs(this.data?.invoice?.issue_date);
    },
    dueDateLabel() {
      return formatMs(this.data?.invoice?.due_date);
    },
    pdfUrl() {
      return publicInvoiceApiBase() + '/api/public/invoice/' + this.token + '/pdf';
    },
    showPayButton() {
      return this.data?.starqris_enabled && this.data?.invoice?.status !== 'paid';
    },
    manualTransferBanks() {
      const mt = this.data?.manual_transfer;
      if (!mt) return [];
      if (Array.isArray(mt.banks) && mt.banks.length) return mt.banks;
      if (mt.bank_name || mt.bank_account_number) {
        return [{
          bank_name: mt.bank_name,
          bank_account_number: mt.bank_account_number,
          bank_account_holder: mt.bank_account_holder
        }];
      }
      return [];
    },
    manualTransferNotes() {
      const mt = this.data?.manual_transfer;
      if (!mt || !mt.notes) return '';
      return typeof mt.notes === 'string' ? mt.notes : '';
    }
  },
  mounted() {
    this.token = this.$route.params.token;
    document.body.classList.add('pub-inv-body');
    this.loadInvoice();
  },
  beforeDestroy() {
    document.body.classList.remove('pub-inv-body');
  },
  methods: {
    formatRp,
    statusMeta,
    lineAmount(it) {
      if (it.amount != null) return it.amount;
      return Number(it.qty || 0) * Number(it.unit_price || 0);
    },
    loadInvoice() {
      const url = publicInvoiceApiBase() + '/api/public/invoice/' + this.token;
      axios.get(url, { headers: { Authorization: '' } })
        .then(r => {
          this.data = r.data.data;
          if (this.data.invoice.qris_payload) {
            this.qrisPayload = this.data.invoice.qris_payload;
          }
        })
        .catch(() => { this.loadError = true; });
    },
    pay() {
      this.paying = true;
      const url = publicInvoiceApiBase() + '/api/public/invoice/' + this.token + '/pay';
      axios.post(url, {}, { headers: { Authorization: '' } })
        .then(r => {
          this.qrisPayload = r.data.data.qris_payload || '';
          if (!this.qrisPayload && this.$toast) {
            this.$toast.info('QRIS belum tersedia');
          }
        })
        .catch(() => { if (this.$toast) this.$toast.error('Gagal memuat QRIS'); })
        .finally(() => { this.paying = false; });
    },
    copyQris() {
      this.copyText(this.qrisPayload, 'Kode QRIS');
    },
    copyText(text, label) {
      if (!text) return;
      navigator.clipboard.writeText(String(text));
      if (this.$toast) this.$toast.success(label + ' disalin');
    }
  }
};
</script>

<style>
body.pub-inv-body {
  margin: 0;
  background: #0f1222 !important;
}
</style>

<style scoped>
.pub-inv {
  min-height: 100vh;
  padding: 24px 16px 40px;
  background: radial-gradient(ellipse 120% 80% at 50% -20%, #3d42a8 0%, #0f1222 55%, #0a0c16 100%);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: #1e2132;
  box-sizing: border-box;
}
.pub-inv--center {
  display: flex;
  align-items: center;
  justify-content: center;
}
.pub-inv__shell {
  max-width: 720px;
  margin: 0 auto;
}
.pub-inv__shell--narrow {
  max-width: 420px;
}
.pub-inv__hero {
  background: linear-gradient(135deg, #5f63f2 0%, #4347d8 50%, #3a3fc9 100%);
  border-radius: 20px 20px 0 0;
  padding: 28px 28px 24px;
  color: #fff;
  box-shadow: 0 20px 50px rgba(95, 99, 242, 0.35);
}
.pub-inv__hero-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}
.pub-inv__brand {
  display: flex;
  gap: 16px;
  align-items: center;
  min-width: 0;
}
.pub-inv__logo {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  padding: 6px;
}
.pub-inv__company {
  margin: 0 0 4px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
  letter-spacing: -0.02em;
}
.pub-inv__company-meta {
  margin: 0;
  font-size: 0.8rem;
  opacity: 0.88;
  line-height: 1.4;
  color: #fff;
}
.pub-inv__hero-badge {
  flex-shrink: 0;
}
.pub-inv__status {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.35);
}
.pub-inv__status--paid {
  background: rgba(32, 201, 151, 0.35);
  border-color: rgba(255, 255, 255, 0.5);
}
.pub-inv__status--sent {
  background: rgba(255, 255, 255, 0.28);
}
.pub-inv__status--cancelled {
  background: rgba(255, 100, 100, 0.35);
}
.pub-inv__hero-bottom {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}
.pub-inv__label {
  display: block;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  opacity: 0.75;
  margin-bottom: 4px;
  color: #fff;
}
.pub-inv__inv-no {
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
}
.pub-inv__dates {
  display: flex;
  gap: 24px;
  text-align: right;
  font-size: 0.9rem;
  color: #fff;
}
.pub-inv__body {
  background: #f8f9fc;
  padding: 24px;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
}
.pub-inv__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}
@media (max-width: 576px) {
  .pub-inv__grid {
    grid-template-columns: 1fr;
  }
  .pub-inv__dates {
    text-align: left;
    width: 100%;
  }
}
.pub-inv__card {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  border: 1px solid #e8eaf3;
}
.pub-inv__card--total {
  background: linear-gradient(145deg, #fff 0%, #f3f4ff 100%);
  border-color: #d8dcff;
}
.pub-inv__section-title {
  margin: 0 0 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #868eae;
}
.pub-inv__customer-name {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #272b41;
}
.pub-inv__amount {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  color: #5f63f2;
  letter-spacing: -0.03em;
}
.pub-inv__paid-note {
  margin: 10px 0 0;
  font-size: 0.85rem;
  color: #0d9467;
  font-weight: 600;
}
.pub-inv__card--table {
  margin-bottom: 16px;
  padding-bottom: 8px;
}
.pub-inv__table-wrap {
  overflow-x: auto;
  margin: 0 -4px 16px;
}
.pub-inv__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.pub-inv__table thead th {
  padding: 10px 12px;
  text-align: left;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #868eae;
  border-bottom: 2px solid #eef0f7;
}
.pub-inv__table tbody td {
  padding: 14px 12px;
  border-bottom: 1px solid #f0f2f8;
  color: #272b41;
}
.pub-inv__table tbody tr:last-child td {
  border-bottom: none;
}
.pub-inv__col-qty {
  text-align: center;
  width: 56px;
}
.pub-inv__col-num {
  text-align: right;
  white-space: nowrap;
}
.pub-inv__totals {
  max-width: 280px;
  margin-left: auto;
  padding-top: 8px;
}
.pub-inv__totals-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 0.9rem;
  color: #5a5f7d;
}
.pub-inv__totals-row--grand {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 2px solid #eef0f7;
  font-size: 1.1rem;
  font-weight: 800;
  color: #272b41;
}
.pub-inv__notes {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #5a5f7d;
  white-space: pre-wrap;
}
.pub-inv__card--bank {
  background: linear-gradient(145deg, #fff 0%, #f8f9ff 100%);
  border-color: #c9ccf5;
}
.pub-inv__bank-block {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px dashed #e3e6ef;
}
.pub-inv__bank-block:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.pub-inv__bank-block-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #5f63f2;
  margin-bottom: 10px;
}
.pub-inv__bank-lead {
  font-size: 0.9rem;
  color: #5a5f7d;
  margin: -4px 0 16px;
}
.pub-inv__bank-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pub-inv__bank-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e8eaf3;
}
.pub-inv__bank-row span:first-child {
  flex: 0 0 100%;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #868eae;
  font-weight: 700;
}
@media (min-width: 480px) {
  .pub-inv__bank-row span:first-child {
    flex: 0 0 110px;
  }
}
.pub-inv__bank-row strong {
  flex: 1;
  font-size: 1.05rem;
  color: #272b41;
}
.pub-inv__copy {
  border: none;
  background: #eef0ff;
  color: #5f63f2;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
}
.pub-inv__bank-notes {
  margin: 16px 0 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #5a5f7d;
  white-space: pre-wrap;
  padding-top: 12px;
  border-top: 1px dashed #e3e6ef;
}
.pub-inv__card--qris {
  margin-bottom: 0;
}
.pub-inv__qris-hint {
  font-size: 0.85rem;
  color: #868eae;
  margin: -4px 0 12px;
}
.pub-inv__qris-code {
  width: 100%;
  font-family: ui-monospace, monospace;
  font-size: 0.75rem;
  border-radius: 10px;
  border: 1px solid #e3e6ef;
  padding: 12px;
  margin-bottom: 10px;
  resize: none;
}
.pub-inv__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  padding: 0 8px;
}
.pub-inv__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  text-decoration: none !important;
  transition: transform 0.15s, box-shadow 0.15s;
}
.pub-inv__btn:active {
  transform: scale(0.98);
}
.pub-inv__btn--primary {
  background: linear-gradient(135deg, #20c997 0%, #12a87a 100%);
  color: #fff !important;
  box-shadow: 0 8px 24px rgba(32, 201, 151, 0.4);
}
.pub-inv__btn--outline {
  background: #fff;
  color: #5f63f2 !important;
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}
.pub-inv__btn--ghost {
  background: #f4f5f9;
  color: #5a5f7d !important;
}
.pub-inv__btn--block {
  width: 100%;
}
.pub-inv__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.pub-inv__footer-note {
  text-align: center;
  margin: 20px 0 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
}
.pub-inv__error-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: #fee;
  color: #cf1322;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}
</style>
